import { AlertTriangle } from 'lucide-react'

import { useI18n } from '@/i18n'
import { cn } from '@/lib/cn'
import { parseBackendDateTime } from '@/lib/format/datetime'

export interface SourceStatus {
  provider: string
  status: string
  error: string | null
  fetched_at: string
  model_count: number
}

export interface SourceHealthBannerProps {
  sources?: readonly SourceStatus[]
  className?: string
}

export function SourceHealthBanner({ sources = [], className }: SourceHealthBannerProps) {
  const { language, t } = useI18n()
  const unavailableSources = sources.filter((source) => source.status !== 'ok')

  if (unavailableSources.length === 0) return null

  const dateFormatter = new Intl.DateTimeFormat(language === 'zh' ? 'zh-CN' : 'en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  })

  return (
    <div
      role="alert"
      className={cn(
        'flex gap-2.5 border-y border-warning/40 bg-warning/10 px-3 py-2.5 text-sm text-foreground',
        className,
      )}
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="font-medium text-warning">{t('modelSelect.sourceHealthWarning')}</p>
        <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
          {unavailableSources.map((source) => (
            <li key={source.provider} className="flex flex-wrap items-center gap-x-1.5">
              <span className="font-medium text-foreground">{source.provider}</span>
              <span aria-hidden>·</span>
              <time dateTime={source.fetched_at}>
                {t('modelSelect.sourceHealthFetchedAt', {
                  time: dateFormatter.format(parseBackendDateTime(source.fetched_at)),
                })}
              </time>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
